const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
const CALENDAR_DATE_DISPLAY_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:T.+)?$/u;

/**
 * Bare `YYYY-MM-DD` values from the API are calendar dates, but `new Date()`
 * reads them as UTC midnight, which lands on the previous day anywhere west of
 * Greenwich. Parse those as local calendar dates so the day never shifts.
 */
function parseDate(dateTimeString: string): Date {
  const dateOnly = DATE_ONLY_PATTERN.exec(dateTimeString);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3])
    );
  }

  return new Date(dateTimeString);
}

function toUsDateLabel(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a datetime string to US date format (short format)
 * @param dateTimeString - ISO datetime string
 * @returns Formatted date like "Jun 23, 2026"
 */
export function formatUsDate(dateTimeString: string): string {
  const date = parseDate(dateTimeString);
  if (Number.isNaN(date.getTime())) {
    return dateTimeString;
  }

  return toUsDateLabel(date);
}

/**
 * Format an ISO ship date by its calendar date, without interpreting its time
 * or offset. Non-ISO labels (including existing slash dates) pass through.
 */
export function formatCalendarDateForDisplay(value: string): string {
  const match = CALENDAR_DATE_DISPLAY_PATTERN.exec(value);
  const calendarDate = match?.[1];
  if (!calendarDate) return value;

  const parsedCalendarDate = new Date(`${calendarDate}T00:00:00.000Z`);
  if (
    Number.isNaN(parsedCalendarDate.getTime()) ||
    parsedCalendarDate.toISOString().slice(0, 10) !== calendarDate ||
    (value !== calendarDate && Number.isNaN(Date.parse(value)))
  ) {
    return value;
  }

  return formatUsDate(calendarDate);
}

/** Midnight on the Monday of the ISO week containing the given date. */
function startOfIsoWeek(date: Date): Date {
  const monday = new Date(date);
  monday.setHours(0, 0, 0, 0);
  // getDay() is 0 for Sunday, which in ISO terms closes a week that began six
  // days earlier rather than opening a new one.
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
}

/**
 * ISO 8601 week number. Week 1 is the week containing the first Thursday of the
 * year, so the Thursday of a week also determines which year the week counts in.
 */
function isoWeekNumber(date: Date): number {
  const thursday = startOfIsoWeek(date);
  thursday.setDate(thursday.getDate() + 3);

  const firstThursday = new Date(thursday.getFullYear(), 0, 1);
  firstThursday.setDate(
    firstThursday.getDate() + ((4 - firstThursday.getDay() + 7) % 7)
  );

  const weeksBetween =
    (thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000);
  // Round rather than floor: a DST transition inside the span leaves the
  // difference an hour short of a whole number of weeks.
  return Math.round(weeksBetween) + 1;
}

/**
 * Label the week an arrival falls in, so a date can be read as a planning week.
 * Weeks start Monday (ISO 8601).
 * @param dateTimeString - ISO datetime string
 * @returns Label like "Week of Feb 8, 2027 (Week 6)"
 */
export function formatArrivalWeek(dateTimeString: string): string {
  const date = parseDate(dateTimeString);
  if (Number.isNaN(date.getTime())) {
    return dateTimeString;
  }

  const monday = startOfIsoWeek(date);
  return `Week of ${toUsDateLabel(monday)} (Week ${String(isoWeekNumber(date))})`;
}

/**
 * Calculate the number of days from today to the given date
 * @param dateTimeString - ISO datetime string
 * @returns Number of days (0 if date is today or earlier)
 */
export function calculateDaysOut(dateTimeString: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const arrival = parseDate(dateTimeString);
  if (Number.isNaN(arrival.getTime())) {
    return 0;
  }

  const diffMs = arrival.getTime() - today.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

/**
 * Alias for calculateDaysOut (for backwards compatibility)
 */
export function daysBetween(estArrival: string): number {
  return calculateDaysOut(estArrival);
}

/**
 * Alias for formatUsDate (for backwards compatibility)
 */
export function formatDate(d: string): string {
  return formatUsDate(d);
}

/**
 * Calculate stockout bar width percentage (capped at 100%)
 */
export function stockoutBarWidth(days: number): string {
  return `${String(Math.min(100, days * 5))}%`;
}

/**
 * Get stockout bar color based on days until stockout
 */
export function stockoutBarColor(days: number): string {
  if (days <= 3) return "var(--rtg-red, #dc3545)";
  if (days <= 14) return "#f5d050";
  return "var(--rtg-green, #389162)";
}
